import { useState, useEffect } from "react";
import { useAdmin } from "@/context/AdminContext";
import { Save, Loader2, Settings, Phone, Mail, MapPin, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const AdminSettingsPage = () => {
  const { apiGet, apiPut } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    whatsapp_number: "",
    store_name: "",
    store_email: "",
    store_phone: "",
    store_address: "",
    delivery_charge: "",
    free_delivery_above: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await apiGet("/settings");
      setForm({
        whatsapp_number: data.whatsapp_number || "",
        store_name: data.store_name || "",
        store_email: data.store_email || "",
        store_phone: data.store_phone || "",
        store_address: data.store_address || "",
        delivery_charge: data.delivery_charge?.toString() || "50",
        free_delivery_above: data.free_delivery_above?.toString() || "500",
      });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiPut("/settings", {
        whatsapp_number: form.whatsapp_number,
        store_name: form.store_name,
        store_email: form.store_email,
        store_phone: form.store_phone,
        store_address: form.store_address,
        delivery_charge: parseFloat(form.delivery_charge) || 50,
        free_delivery_above: parseFloat(form.free_delivery_above) || 500,
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="w-6 h-6" />
          Store Settings
        </h1>
        <p className="text-muted-foreground">Configure your store details and preferences</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* WhatsApp */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp Settings
          </h2>
          <div>
            <Label htmlFor="whatsapp_number">WhatsApp Number (with country code, no +)</Label>
            <Input
              id="whatsapp_number"
              value={form.whatsapp_number}
              onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
              placeholder="919441114344"
              className="mt-1"
              data-testid="input-whatsapp-number"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Format: Country code + number without spaces or +. Example: 919441114344
            </p>
          </div>
        </div>

        {/* Store Info */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold mb-4">Store Information</h2>
          <div className="grid gap-4">
            <div>
              <Label htmlFor="store_name">Store Name</Label>
              <Input id="store_name" value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} className="mt-1" data-testid="input-store-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="store_email">Email</Label>
                <Input id="store_email" value={form.store_email} onChange={(e) => setForm({ ...form, store_email: e.target.value })} className="mt-1" data-testid="input-store-email" />
              </div>
              <div>
                <Label htmlFor="store_phone">Phone</Label>
                <Input id="store_phone" value={form.store_phone} onChange={(e) => setForm({ ...form, store_phone: e.target.value })} className="mt-1" data-testid="input-store-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="store_address">Address</Label>
              <Textarea id="store_address" value={form.store_address} onChange={(e) => setForm({ ...form, store_address: e.target.value })} rows={2} className="mt-1" data-testid="input-store-address" />
            </div>
          </div>
        </div>

        {/* Delivery Settings */}
        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Delivery Settings
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="delivery_charge">Delivery Charge (₹)</Label>
              <Input id="delivery_charge" type="number" value={form.delivery_charge} onChange={(e) => setForm({ ...form, delivery_charge: e.target.value })} className="mt-1" data-testid="input-delivery-charge" />
            </div>
            <div>
              <Label htmlFor="free_delivery_above">Free Delivery Above (₹)</Label>
              <Input id="free_delivery_above" type="number" value={form.free_delivery_above} onChange={(e) => setForm({ ...form, free_delivery_above: e.target.value })} className="mt-1" data-testid="input-free-delivery-above" />
            </div>
          </div>
        </div>

        <Button type="submit" size="lg" className="w-full btn-primary" disabled={saving} data-testid="save-settings-button">
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save Settings</>
          )}
        </Button>
      </form>
    </div>
  );
};

export default AdminSettingsPage;
